/**
 * Wire types for Phase 5d Receipts management.
 *
 * Mirrors `pos-api/src/pos/receipts/receipts.schema.ts`. When the
 * backend shape changes, update both ends.
 */

/**
 * Filter scope for the receipts list:
 *
 *   - 'current_shift'  Default. Returns receipts that belong to the
 *                      cashier's currently-open Inv_CashDrawerHistory
 *                      session. If no open shift, returns an empty list.
 *
 *   - 'all'            All-time list (subject to search). Used when
 *                      the cashier is looking up an old receipt for
 *                      view-only access. Edit is still gated by the
 *                      same 4 rules — older receipts will simply
 *                      come back with `can_edit: false`.
 */
export type ReceiptListScope = 'current_shift' | 'all';

/**
 * Document-type filter for the receipts list — the admin Orders screen's
 * All / Sales / Returns toggle. `sales` is Codes 51 + 52 (a credit-sales
 * invoice is a sale), `returns` is Code 53, `all` is the three.
 */
export type ReceiptListType = 'all' | 'sales' | 'returns';

/**
 * Order-type filter for the receipts lists (till Receipts screen and admin
 * Orders): `Inv_Receipt_h.OrderType` 1-5, or `none` for a receipt that
 * carries no order type (the ERP writes NULL). `all` / absent = no filter.
 */
export type ReceiptOrderTypeFilter = 'all' | '1' | '2' | '3' | '4' | '5' | 'none';

/**
 * Every order type, in the order the filter lists them. The codes are the
 * legacy numbering the whole stack charges on (cashier lib/orderTypeCode.ts);
 * the names are the admin's (promotions ORDER_TYPES), so the two apps read
 * the same.
 */
export const RECEIPT_ORDER_TYPES: ReadonlyArray<{
  value: Exclude<ReceiptOrderTypeFilter, 'all'>;
  en: string;
  ar: string;
}> = [
  { value: '1', en: 'Walk-in / Takeaway', ar: 'محلي / سفري' },
  { value: '2', en: 'Delivery', ar: 'توصيل' },
  { value: '3', en: 'Dine-in', ar: 'صالة' },
  { value: '4', en: 'Pickup', ar: 'استلام' },
  { value: '5', en: 'Membership', ar: 'عضوية' },
  { value: 'none', en: 'Not set', ar: 'غير محدد' },
];

/**
 * The name of a receipt's order type (Inv_Receipt_h.OrderType) in one
 * language, for a list column: '' for none (the cell shows its own dash), the
 * code itself for one the stack does not model (6 = work order).
 */
export function receiptOrderTypeName(code: number | null | undefined, lang: 'en' | 'ar'): string {
  if (code == null) return '';
  const hit = RECEIPT_ORDER_TYPES.find((o) => o.value === String(code));
  return hit ? hit[lang] : `#${code}`;
}

/** The name of an order-type filter value in one language; '' for 'all'. */
export function receiptOrderTypeLabel(v: ReceiptOrderTypeFilter | undefined, lang: 'en' | 'ar'): string {
  const hit = RECEIPT_ORDER_TYPES.find((o) => o.value === v);
  return hit ? hit[lang] : '';
}

/**
 * Search modes — derived from the query string. The frontend just
 * sends `search=...`; the server detects intent (numeric → entry_no,
 * else → customer name/phone).
 */
export interface ListReceiptsRequestQuery {
  scope?: ReceiptListScope;
  /** Absent = 'all'. */
  type?: ReceiptListType;
  /** Inv_Receipt_h.OrderType 1-5, or 'none' (no order type). Absent / 'all' = every type. */
  order_type?: ReceiptOrderTypeFilter;
  /** Inv_PaymentType.id — paid on it alone or as one slice of a split. Absent = every type. */
  payment_type_id?: number;
  search?: string;
  /**
   * Inclusive date window on the receipt's `Entry_Date`, as plain
   * `YYYY-MM-DD` calendar days.
   *
   * Days, not instants. `Entry_Date` is stored as naive tenant wall-clock, so
   * "the 12th" means the tenant's 12th and the server builds the boundaries
   * from these characters directly. Send what the date input gives you —
   * never `toISOString()`, which would shift the window by the browser's
   * offset and lose the first hours of the day for a UTC+n tenant.
   */
  date_from?: string;
  date_to?: string;
  /**
   * Optional time of day on each end, `HH:mm`, in the same naive tenant
   * wall-clock as the dates. Absent = the whole day on that end, which is
   * exactly the window the screen asked for before the filter grew a clock.
   * `time_to` is inclusive of its whole minute.
   */
  time_from?: string;
  time_to?: string;
  /** One customer's receipts — send both or neither (the id spaces overlap). */
  customer_type?: 'cash' | 'credit';
  customer_id?: number;
  page?: number;
  limit?: number;
}

export interface ReceiptListItem {
  /** Inv_Receipt_h.ID */
  id: number;
  /** Inv_Receipt_h.Entry_No (visible "order number" on the receipt) */
  entry_no: number;
  /** ISO datetime string (server-generated, naive wall-clock from MSSQL) */
  entry_date: string;
  /** Inv_Receipt_h.NetValue */
  net_value: number;
  /**
   * Inv_Receipt_h.Reference_No — the free-text reference carried on the
   * receipt (delivery ticket, online order number, offline capture number).
   * Null on an ordinary walk-in sale.
   */
  reference_no: string | null;
  /**
   * Customer name (English fallback to Arabic). Null when walk-in
   * (no customer attached).
   */
  customer_name: string | null;
  /** Inv_CashCustomer.PhoneNumber. Null when walk-in. */
  customer_phone: string | null;
  /**
   * Payment label for the cell — derived from the header's payment_type:
   *   - 'Multi'    when Code = 0 (split payment)
   *   - 'Unpaid'   when Code = -1
   *   - the payment type's name otherwise
   */
  payment_label: string;
  /**
   * Pre-formatted table line ("Sun bed: 5" / "Table: 7") with the tenant's
   * terminology, or null for receipts with no table attached.
   */
  table_label: string | null;
  /** Inv_Receipt_h.OrderType (1-5; null = none). Absent from older API builds. */
  order_type?: number | null;
  /**
   * True if this receipt belongs to the cashier's currently-open
   * Inv_CashDrawerHistory session. Always false when scope='all' for
   * old receipts; true is the precondition for editing.
   */
  in_current_shift: boolean;
  /**
   * The receipt's OWN shift is still open — any cashier's. This, not
   * in_current_shift, is what edit follows (Edit Order covers colleagues'
   * invoices of an open shift). Optional: an older API build does not send it.
   */
  in_open_shift?: boolean;
  /**
   * True if ANY GL_Vouchers row linked to this receipt has Posted=1.
   * A posted voucher means the books are closed for that transaction
   * and editing isn't safe — we error on the side of caution and use
   * "any posted" rather than "all posted" to block edits.
   */
  is_voucher_posted: boolean;
  /**
   * Server-computed: the cashier may open this receipt in edit mode.
   * Combines:
   *   1. cashier has PERM_EDIT_ORDER (385)
   *   2. there's an open shift for this cashier
   *   3. the receipt is within that open shift
   *      (matching employee + drawer, ShiftSerialNumber still null,
   *       Entry_Date >= shift open time)
   *   4. no related voucher is posted
   *
   * Frontend hides/disables the Edit button when false. The actual
   * edit endpoint (Phase 5d.4) re-validates these rules — never trust
   * the client's `can_edit` for authorization.
   */
  can_edit: boolean;
  /** True for a return/exchange receipt (Inv_Receipt_h.Code = 53). */
  is_return: boolean;
  /**
   * True for a credit-sales invoice (Inv_Receipt_h.Code = 52): a credit
   * customer's on-account sale, booked on their GL account and settled later.
   * Absent from older servers.
   */
  is_credit?: boolean;
  /**
   * `Inv_Receipt_h.Is_Canceled` — the receipt was VOIDED.
   *
   * Voided receipts are still LISTED, deliberately: the first thing a cashier
   * does after voiding something is look for it to check it worked, and a row
   * that vanishes reads as data loss. They count for nothing, though — no
   * shift, no Z-report, no report, no analytics, and no admin net total.
   *
   * Absent from older servers; treat a missing value as false.
   */
  is_canceled?: boolean;
  /**
   * Server-computed: may this cashier void this receipt right now.
   *
   * Never the authorization — the endpoint recomputes every gate on the way
   * in. This only decides whether the button is offered.
   */
  can_void?: boolean;
  /**
   * Short English reason when `can_void` is false; null when it is true.
   * Shown as the disabled button's tooltip so a cashier is told WHY rather
   * than left pressing a dead control.
   */
  void_blocked_reason?: string | null;
}

/**
 * One row of GET /api/v1/receipts/cancel-reasons — the ERP's
 * `GL_OrderCancelationReasons`, which is what the legacy till's
 * `ddlCancelReason` dropdown is filled from.
 *
 * Both names are NOT NULL in the ERP and on most tenants hold the SAME
 * string, so a caller picks one and falls back to the other rather than
 * showing a label twice. The list can legitimately be EMPTY: two POS tenants
 * have no rows, and on those the till asks for a note alone.
 */
export interface CancelReason {
  id: number;
  name: string | null;
  name_ar: string | null;
}

/**
 * Body for POST /api/v1/receipts/:id/void.
 *
 * `cancel_reason_id` is a row from `cancel-reasons` and `note` is the free
 * text box beside the picker. BOTH are optional — a void with neither is a
 * complete request, and the legacy dialog's "Please Select Cancellation
 * Reason!" is deliberately not reproduced. An id that IS sent must exist on
 * the tenant; the column behind it carries a foreign key.
 */
export interface VoidReceiptRequest {
  cancel_reason_id?: number | null;
  note?: string | null;
  /**
   * The free-text reason this endpoint took before it had a picker. Still
   * accepted, and read as a `note`, so a till that has not been redeployed
   * yet keeps voiding against a newer API.
   *
   * @deprecated send `note` (and `cancel_reason_id`).
   */
  reason?: string | null;
}

/** Who voided a receipt, when and why — `pos_receipt_void_audit`. */
export interface ReceiptVoidInfo {
  id: number;
  receipt_id: number;
  entry_no: number | null;
  reason: string | null;
  voided_by: number;
  voided_by_name: string | null;
  /** The shift the RECEIPT belonged to — the Z-report whose numbers moved. */
  pos_shift_id: number | null;
  /** Naive tenant wall-clock, like every other timestamp on the wire. */
  voided_at: string;
}

export interface VoidReceiptResponse {
  receipt_id: number;
  entry_no: number;
  /**
   * True when THIS call voided it; false on an idempotent replay — a double
   * tap, a retried request, or a second till that got there first. Both are
   * success; only the message differs.
   */
  voided_now: boolean;
  /** Null for a receipt the ERP cancelled, which recorded no attribution. */
  audit: ReceiptVoidInfo | null;
}

export interface ListReceiptsResponse {
  data: ReceiptListItem[];
  page: number;
  limit: number;
  total: number;
  /**
   * Diagnostic: did the server find an open shift for this cashier?
   * The frontend uses this to render an empty-state message
   * ("No open shift — open a shift to see today's receipts") instead
   * of a generic "no results".
   */
  has_open_shift: boolean;
}

export interface PatchReceiptRequest {
  memo?: string | null;
}

/**
 * Body for PUT /api/v1/receipts/:id — full order replacement.
 *
 * Same shape as CreateOrderRequest minus idempotency_key and branch_id
 * (those come from the URL param and the receipt's own branch).
 */
export interface ReplaceReceiptRequest {
  items: Array<{
    item_id: number;
    qty: number;
    /** Unit price INCLUDING modifier price deltas, BEFORE line discount. */
    price: number;
    /** The unit the line sells in; omitted = the item's selling unit. */
    unit_id?: number;
    discount_value?: number;
    discount_percent?: number;
    notes?: string | null;
    /** FE-computed line total = qty * price - discount_value. Server re-checks. */
    total: number;
    /**
     * Who sold the line. An edit rewrites every line, so these travel with it
     * or the rebuilt rows come back with `SalesManId` NULL — and the
     * Sales-by-Salesman report filters that column out.
     */
    salesman_id?: number | null;
    salesman2_id?: number | null;
    modifiers?: Array<{
      option_id: number;
      group_id: number;
      group_name: string;
      name: string;
      name_ar?: string | null;
      price_delta: number;
    }>;
  }>;
  customer?: { customer_id?: number; customer_phone?: string; customer_name?: string; customer_name_ar?: string } | null;
  payments: Array<{ type_code: number; amount: number }>;
  cash_tendered?: number;
  discount_value?: number;
  discount_percent?: number;
  delivery_fee?: number;
  /** Percentage (e.g. 15 for 15%). Defaults to 15 on the server. */
  tax_percentage?: number;
  /** FE-claimed totals — re-verified server-side (tolerance ±0.01). */
  expected_subtotal: number;
  expected_tax: number;
  expected_total: number;
  order_type?: number | null;
  reference_no?: string | null;
  note?: string | null;
  delivery?: {
    customer_id: number;
    address_id: number;
    driver_id?: number | null;
    delivery_fees: number;
    delivery_date: string;
    reference_no?: string | null;
  };
  discount_reason_id?: number | null;
  manager_override_user_id?: number | null;
  /** "Change Salesman" — re-credit the edited receipt. Absent = unchanged. */
  employee_id?: number | null;
  coupon_code?: string | null;
}

// ── Held (parked) invoices — /api/v1/held-orders ─────────────────────────

/** One held invoice (Inv_Receipt_h.IsHolded = 1) on the Held Orders screen. */
export interface HeldOrderRow {
  id: number;
  entry_no: number;
  entry_date: string;
  net_value: number;
  order_type: number | null;
  customer_name: string | null;
  customer_name_ar: string | null;
  customer_phone: string | null;
  table_number: string | number | null;
  notes: string | null;
  item_count: number;
  employee_id: number | null;
  employee_name: string | null;
  /** Who has this held order open in a till RIGHT NOW (null = nobody). */
  open_by?: HeldOrderPresence | null;
}

export interface HeldOrderPresence {
  user_id: number;
  user_name: string | null;
  at: string;
}

export interface ListHeldOrdersResponse {
  data: HeldOrderRow[];
  scope: 'mine' | 'branch';
  /** The cashier holds "View All Branch Holded Orders". */
  can_view_branch: boolean;
}

export interface PayHeldOrderRequest {
  payments: Array<{ type_code: number; amount: number }>;
  cash_tendered?: number;
}

export interface PayHeldOrderResponse {
  order_id: number;
  entry_no: number;
  entry_date: string;
  totals: {
    subtotal: number;
    discount_value: number;
    delivery_fee: number;
    tax_value: number;
    net_value: number;
  };
  change_amount: number;
  pos_shift_id: number;
  print_count: number;
  idempotent_replay: boolean;
}

/** Re-park a held invoice after it was changed in the cart: the edit body minus the tender. */
export type UpdateHeldOrderRequest = Omit<ReplaceReceiptRequest, 'payments' | 'cash_tendered'> & {
  /** Move the held order to this table; null takes it off; absent keeps it. */
  branch_table_id?: number | null;
  /** The manager's (LoginName 'Admin') password — needed when items are removed. */
  manager_password?: string;
  /** Send the items added since the last hold to the kitchen. */
  print_new_items?: boolean;
};

export interface CancelHeldOrderRequest {
  manager_password: string;
  cancel_reason_id?: number | null;
  note?: string | null;
}

export interface UpdateHeldOrderResponse {
  order_id: number;
  entry_no: number;
  entry_date: string;
  totals: PayHeldOrderResponse['totals'];
  /** Lines sent to the kitchen as ADDED ITEMS. */
  printed_new_items?: number;
}
